import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import moment from 'moment';
import { Button } from 'react-bootstrap';

import Icon, { icons } from '../../generic/Icon';
import ModalDateRangePicker from '../../modals/ModalDateRangePicker';
import PaginationControl from '../../generic/Pagination/PaginationControl';
import { RouterLink } from '../../generic';
import InfoPanel from '../../generic/InfoPanel';
import Flex from '../../generic/Flex';

type Props = {
  startDate?: string;
  endDate?: string;
  onSetDateRange: (range: {endDate: Date, startDate: Date}) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageNext: () => void;
  onPagePrevious: () => void;
  isLoading: boolean;
};

const ActivityMovedNotice = () => (
  <InfoPanel>
    <div className="fossa-font-size-sm">
      Project activity metadata has been moved to
      <RouterLink
        to="/account/settings/audit-logs"
        className="margin-left-xs"
        variant="primary"
      >
        <span style={{ textDecoration: 'underline' }}>Audit Logs</span>
      </RouterLink>
    </div>
  </InfoPanel>
);

const BuildListHeader: VoidFunctionComponent<Props> = ({
  startDate,
  endDate,
  onSetDateRange,
  onPagePrevious,
  onPageNext,
  page,
  pageSize,
  totalCount,
  isLoading,
}) => {
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);

  // Date Picker config
  const sDate = moment(startDate);
  const eDate = moment(endDate);
  const start = sDate.format('MMMM Do');
  const end = eDate.format('MMMM Do');
  let dateLabel = `${start} - ${end}`;

  if (start === end) {
    dateLabel = start;
  }

  return (
    <div>
      <div className="panel-heading">
        <Flex justify="space-between">
          <div>
            <ActivityMovedNotice />
          </div>
          <div>
            <Button onClick={() => setDatePickerOpen(true)}>
              <span className="padding-right-sm">
                {dateLabel}
              </span>
              <Icon icon={icons.chevronDown} />
            </Button>
            <PaginationControl
              icon={icons.caretLeft}
              isDisabled={page < 1}
              onClick={onPagePrevious}
            />
            {isLoading
              ? <span>...</span>
              : <PaginationRange
                  pageSize={pageSize}
                  page={page}
                  totalCount={totalCount}
                />}
            <PaginationControl
              icon={icons.caretRight}
              isDisabled={(pageSize * (page + 1)) >= totalCount}
              onClick={onPageNext}
            />
          </div>
        </Flex>
      </div>
      <ModalDateRangePicker
        show={isDatePickerOpen}
        onSetDateRange={onSetDateRange}
        startDate={startDate}
        endDate={endDate}
        onHide={() => setDatePickerOpen(false)}
      />
    </div>
  );
};

export default BuildListHeader;

type PaginationRangeProps = {
  page: number;
  pageSize: number;
  totalCount: number;
};

const PaginationRange = ({
  page,
  pageSize,
  totalCount,
}: PaginationRangeProps) => {
  if (totalCount === 0) {
    return null;
  }

  const rangeStart = (page * pageSize) + 1;
  const rangeEnd = (page + 1) * pageSize;

  return (
    <span>
      {`${rangeStart} - ${rangeEnd < totalCount ? rangeEnd : totalCount} of ${totalCount.toLocaleString()}`}
    </span>
  );
};
